import { create } from "zustand";
import type { Carrier } from "@/features/auth/lib/signup/identity/carriers";
import type { Credit4uTwoWayInfo } from "@/features/auth/api/credit4uApi";
import type { CreditRegisterMode } from "@/features/auth/model/types";

/**
 * 본인인증 입력 초안.
 * 인증번호 만료 후 input step 복귀 시 값이 자동 복원되어야 하므로 store 에서 관리한다.
 * 민감정보이므로 persist 미사용 (메모리에만 유지).
 */
type SignupIdentityDraft = {
  name: string;
  residentIdFront: string;
  residentIdBack: string;
  carrier: Carrier | "";
  phone: string;
};

type SignupSessionState = {
  identityDraft: SignupIdentityDraft;
  setIdentityDraft: (patch: Partial<SignupIdentityDraft>) => void;

  /**
   * 본인확인 step에서 시작한 register 2-Way 세션.
   * identity step 과 register step 이 동일한 CODEF 세션을 이어가도록
   * 여기서 공유한다 (jobIndex/threadIndex/jti/twoWayTimestamp/method/extraInfo).
   */
  registerTwoWay: Credit4uTwoWayInfo | null;
  setRegisterTwoWay: (v: Credit4uTwoWayInfo | null) => void;

  /**
   * staged 흐름 세션 상관 ID.
   * 클라이언트가 1개 생성해 전 호출에 동일값을 전달한다.
   */
  checkParamUUID: string | null;
  setCheckParamUUID: (v: string | null) => void;

  /**
   * EntryStep 선택값 — auto | manual.
   * 본인확인 step 에서 미리 선택해 register step 이 이어받는다.
   */
  registerMode: CreditRegisterMode | null;
  setRegisterMode: (v: CreditRegisterMode | null) => void;

  /**
   * STEP1(type="1", autoRegister=true) 응답의 서버 생성 candidate 자격증명 (1회 노출 평문).
   * 이후 sms/etc(자동)/email 호출에 echo 필수 (§8-6-1). manual 요청에는 동봉하지 않는다.
   * 민감정보이므로 persist 금지. 메모리에만 유지.
   */
  autoCredentials: { id: string; password: string } | null;
  setAutoCredentials: (v: { id: string; password: string } | null) => void;

  /**
   * 인증번호 시도 단위 정리.
   * registerTwoWay 를 null 로 리셋해 2-Way 세션을 초기화한다.
   * identityDraft/checkParamUUID/registerMode/autoCredentials 는 유지한다.
   * (OTP 재시도 후 동일 세션 컨텍스트를 이어가야 하므로.)
   */
  clearIdentityOtpSession: () => void;

  /** identityDraft 전체 리셋 — 가입 완료(finish) 또는 플로우 이탈 시 호출. */
  clearSignupSession: () => void;

  /**
   * 세션(registerTwoWay/checkParamUUID/registerMode/autoCredentials)만 정리하고
   * identityDraft(입력값)는 유지 — 만료/에러로 본인인증 첫 화면으로 되돌릴 때(재시작) 입력 보존.
   */
  clearSignupSessionKeepDraft: () => void;
};

const INITIAL_DRAFT: SignupIdentityDraft = {
  name: "",
  residentIdFront: "",
  residentIdBack: "",
  carrier: "",
  phone: "",
};

export const useSignupSessionStore = create<SignupSessionState>((set) => ({
  identityDraft: INITIAL_DRAFT,

  setIdentityDraft: (patch) =>
    set((state) => ({
      identityDraft: { ...state.identityDraft, ...patch },
    })),

  registerTwoWay: null,
  setRegisterTwoWay: (v) => set({ registerTwoWay: v }),

  checkParamUUID: null,
  setCheckParamUUID: (v) => set({ checkParamUUID: v }),

  registerMode: null,
  setRegisterMode: (v) => set({ registerMode: v }),

  autoCredentials: null,
  setAutoCredentials: (v) => set({ autoCredentials: v }),

  clearIdentityOtpSession: () => set({ registerTwoWay: null }),

  clearSignupSession: () =>
    set({
      identityDraft: INITIAL_DRAFT,
      registerTwoWay: null,
      checkParamUUID: null,
      registerMode: null,
      autoCredentials: null,
    }),

  clearSignupSessionKeepDraft: () =>
    set({
      registerTwoWay: null,
      checkParamUUID: null,
      registerMode: null,
      autoCredentials: null,
    }),
}));

/**
 * React hook 컨텍스트 없이 useCallback 내부에서 직접 호출할 수 있는 단독 함수.
 * useCreditRegisterFlow.finish() 에서 사용한다.
 */
export const clearSignupSession = (): void =>
  useSignupSessionStore.getState().clearSignupSession();

/**
 * identityDraft(입력값)를 유지한 채 2-Way 세션만 정리. 만료/에러로 본인인증 첫 화면 재시작 시 사용.
 */
export const clearSignupSessionKeepDraft = (): void =>
  useSignupSessionStore.getState().clearSignupSessionKeepDraft();
